import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Paper,
    Grid,
    Chip,
    CircularProgress,
    Alert,
    List,
    ListItem,
    Popover,
    Button,
    LinearProgress,
    Tooltip
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { getAchievementColor, formatPercent, formatNumber } from '../utils';
import { CATEGORY_LABELS } from '../types';
import TrendingUp from "@mui/icons-material/TrendingUp";
import TrendingDown from "@mui/icons-material/TrendingDown";


const fontSize = {
    xSmall : '0.9rem',
    small : '1.0rem',
    medium : '1.05rem',
    large : '1.1rem',
}

const styles = {

    // 최고 달성률, 최저 달성률, 구독중 업체 카드 전용
    maxMinRatingCard: {
        // [최고 달성률, 최저 달성률, 구독중 업체] 텍스트
        maxMinSubscribingTypography:{
            fontSize: fontSize.xSmall,
            color: '#1e2323',
        },

        // 550%, -100%, 23 ...
        maxMinSubscribingRateOrNum:{
            fontSize: '1.2rem',
            mb: 0.2,
        },

        // 선우문화사, 삼일문화, ...
        companyNameText: {
            // color: '#d32f2f',
            fontSize: fontSize.small,
            display: 'block',
            fontWeight: 500,
            mt: 0.2
        },

        // 순위 표시 버튼
        ranking:{

            button: {
                fontSize: fontSize.xSmall,
                px: 1.5,
                py: 0.5,
                minHeight: 'auto',
                color: '#1976d2',
                borderColor: '#1976d2',
                minWidth: 'auto',
                borderRadius: '20px',
                backgroundColor: '#ffffff',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    backgroundColor: '#1976d2',
                    color: '#ffffff',
                    borderColor: '#1976d2',
                    boxShadow: '0 2px 6px rgba(25, 118, 210, 0.3)',
                    transform: 'translateY(-1px)'
                },
                '&:active': {
                    transform: 'translateY(0px)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                }
            },
        },
    },

    // 가로 퍼센트 Bar LinearProgress 디자인
    progress: {
        height: 10,
        borderRadius: 5,
        marginTop: 2,
        marginBottom: 2,
    },

    // '평균' 텍스트
    averageText:{
        fontSize: fontSize.small,
        fontWeight: 500,
    },
}

const AllKPIStatistics = ({
                                  data,
                                  availableKPIs,
                                  loading,
                                  companies // 업체 데이터 추가
                              }) => {
    const [popoverAnchor, setPopoverAnchor] = useState(null);
    const [selectedIndicatorForRanking, setSelectedIndicatorForRanking] = useState(null);

    // 특정 KPI 지표의 업체별 달성률 순위 가져오기
    const getCompanyRankings = (indicatorCode, categoryCode) => {
        if (!companies) return [];

        const rankings = companies
            .map(company => {
                const subscription = company.subscriptions?.find(sub =>
                    sub.kpiIndicatorCode === indicatorCode &&
                    sub.categoryId === categoryCode &&
                    sub.kpiData?.length > 0
                );

                if (!subscription) return null;

                const latestData = subscription.kpiData[subscription.kpiData.length - 1];
                return {
                    companyName: company.companyName,
                    achievementRate: Math.max(0, latestData.achievementRate || 0), // 음수를 0으로 처리
                    measuredValue: latestData.measuredValue || 0,
                    unit: latestData.unit || '',
                    kpiIndicatorCode: indicatorCode, // KPI 코드
                    categoryId: categoryCode // 카테고리 코드 추가
                };
            })
            .filter(item => item !== null)
            .sort((a, b) => b.achievementRate - a.achievementRate);

        return rankings;
    };

    const getPlusMinusColor = (rate) => {
        return rate >= 100 ? '#2e7d32' : '#d32f2f';
    }

    const getMaxMinPercent = (rate) => {
        const textColor = getPlusMinusColor(rate);
        return (
            <Typography variant="h6" fontWeight="bold" sx={{ ...styles.maxMinRatingCard.maxMinSubscribingRateOrNum, color: textColor}}>
                {formatPercent(rate, 0)}
            </Typography>
        );
    }

    // 트렌딩 화살표 아이콘 결정 함수
    const getTrendingArrow = (rate) => {
        const iconColor = getPlusMinusColor(rate);
        if (rate >= 100){ return ( <TrendingUp sx={{ color: iconColor }} /> )}
        else{ return ( <TrendingDown sx={{ color: iconColor }} /> )}
    };


    // 트렌딩 화살표 아이콘 결정 함수
    const getUpOrDownColorCompanyNameText = (name, rate) => {
        const textColor = getPlusMinusColor(rate);
        return  (
            <Typography variant="caption" sx={{ ...styles.maxMinRatingCard.companyNameText, color: textColor }}>
                {name}
            </Typography>
        )
    };


    // 지부별 KPI 지표 목표 평균 대비 실제 측정값 달성률 계산
    const calculateProgressRate = (indicator) => {
        if (!companies || !indicator) return 0;

        // 해당 지표를 구독하는 회사들의 목표치 평균 계산
        const subscribedCompanies = companies.filter(company => {
            return company.subscriptions?.some(sub =>
                sub.kpiIndicatorCode === indicator.kpiIndicatorCode &&
                sub.categoryId === indicator.categoryCode &&
                sub.kpiData?.length > 0
            );
        });

        if (subscribedCompanies.length === 0) return 0;

        // 목표치 합계 계산
        const totalTargetValue = subscribedCompanies.reduce((sum, company) => {
            const subscription = company.subscriptions?.find(sub =>
                sub.kpiIndicatorCode === indicator.kpiIndicatorCode &&
                sub.categoryId === indicator.categoryCode &&
                sub.kpiData?.length > 0
            );
            return sum + (subscription?.targetValue || 0);
        }, 0);

        // 지표 목표 평균
        const averageTargetValue = totalTargetValue / subscribedCompanies.length;

        // 현재 실제 측정값 평균 대비 목표 평균 달성률
        const currentMeasuredValue = indicator.measuredValue || 0;
        const achievementRate = averageTargetValue > 0 ? (currentMeasuredValue / averageTargetValue) * 100 : 0;

        return achievementRate;
    };

    // 달성표 bar ui 반환 (목표 평균 대비 실제 측정값 달성률 기반)
    const getLinearProgressBar = (indicator) => {
        const progressRate = calculateProgressRate(indicator);
        const progressValue = Math.min(100, Math.max(0, progressRate)); // 막대그래프는 0-100 범위

        return  (
            <LinearProgress
                variant="determinate"
                value={progressValue}
                sx={{
                    ...styles.progress,
                    "& .MuiLinearProgress-bar": {
                        backgroundColor: getAchievementColor(progressRate), // 목표 대비 달성률로 색상 결정
                    },
                }}
            />
        )
    };

    const handleOpenRankingPopover = (event, indicatorCode, categoryCode) => {
        setPopoverAnchor(event.currentTarget);
        setSelectedIndicatorForRanking(`${categoryCode}-${indicatorCode}`);
    };

    const handleCloseRankingPopover = () => {
        setPopoverAnchor(null);
        setSelectedIndicatorForRanking(null);
    };

    // 로딩 상태 - 실제 차트 레이아웃을 유지하면서 스켈레톤 표시
    if (loading) {
        return (
            <Box>
                <Typography variant="h6" fontWeight="600" mb={3} sx={{ color: '#333' }}>
                    지부 전체 달성률 분석
                </Typography>

                <Paper
                    elevation={1}
                    sx={{
                        p: 3,
                        backgroundColor: '#fafafa',
                        border: '1px solid #e0e0e0',
                        borderRadius: '12px',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
                    }}
                >
                    <Grid container spacing={4} alignItems="center">
                        {/* 로딩용 스켈레톤 차트들 */}
                        {[1, 2, 3].map((index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Box sx={{ textAlign: 'center' }}>
                                    {/* KPI 이름 스켈레톤 */}
                                    <Box sx={{
                                        height: '24px',
                                        backgroundColor: '#e0e0e0',
                                        borderRadius: '4px',
                                        mb: 1,
                                        animation: 'pulse 1.5s ease-in-out infinite'
                                    }} />

                                    {/* 카테고리 스켈레톤 */}
                                    <Box sx={{
                                        width: '60px',
                                        height: '20px',
                                        backgroundColor: '#e0e0e0',
                                        borderRadius: '10px',
                                        mb: 2,
                                        mx: 'auto',
                                        animation: 'pulse 1.5s ease-in-out infinite'
                                    }} />

                                    {/* 통계 정보 스켈레톤 */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {/* 평균 달성률 박스 */}
                                        <Box sx={{
                                            backgroundColor: '#f0f0f0',
                                            borderRadius: '12px',
                                            p: 2,
                                            border: '2px solid #e0e0e0',
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}>
                                            <Box sx={{
                                                height: '12px',
                                                backgroundColor: '#e0e0e0',
                                                borderRadius: '4px',
                                                mb: 1,
                                                animation: 'pulse 1.5s ease-in-out infinite'
                                            }} />
                                            <Box sx={{
                                                height: '32px',
                                                backgroundColor: '#d0d0d0',
                                                borderRadius: '4px',
                                                animation: 'pulse 1.5s ease-in-out infinite'
                                            }} />
                                        </Box>

                                        {/* 최고/최저 달성률 */}
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Box sx={{
                                                flex: 1,
                                                backgroundColor: '#f5f5f5',
                                                borderRadius: '8px',
                                                p: 1
                                            }}>
                                                <Box sx={{
                                                    height: '12px',
                                                    backgroundColor: '#e0e0e0',
                                                    borderRadius: '4px',
                                                    mb: 0.5,
                                                    animation: 'pulse 1.5s ease-in-out infinite'
                                                }} />
                                                <Box sx={{
                                                    height: '16px',
                                                    backgroundColor: '#d0d0d0',
                                                    borderRadius: '4px',
                                                    animation: 'pulse 1.5s ease-in-out infinite'
                                                }} />
                                            </Box>
                                            <Box sx={{
                                                flex: 1,
                                                backgroundColor: '#f5f5f5',
                                                borderRadius: '8px',
                                                p: 1
                                            }}>
                                                <Box sx={{
                                                    height: '12px',
                                                    backgroundColor: '#e0e0e0',
                                                    borderRadius: '4px',
                                                    mb: 0.5,
                                                    animation: 'pulse 1.5s ease-in-out infinite'
                                                }} />
                                                <Box sx={{
                                                    height: '16px',
                                                    backgroundColor: '#d0d0d0',
                                                    borderRadius: '4px',
                                                    animation: 'pulse 1.5s ease-in-out infinite'
                                                }} />
                                            </Box>
                                        </Box>

                                        {/* 참여 회사 수 */}
                                        <Box sx={{
                                            height: '12px',
                                            width: '80px',
                                            backgroundColor: '#e0e0e0',
                                            borderRadius: '4px',
                                            mx: 'auto',
                                            animation: 'pulse 1.5s ease-in-out infinite'
                                        }} />
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                    {/* 로딩 텍스트 */}
                    <Box sx={{ textAlign: 'center', mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'inline' }}>
                            KPI 통계 데이터를 불러오는 중입니다...
                        </Typography>
                    </Box>
                </Paper>

                {/* CSS 애니메이션 */}
                <style>
                    {`
            @keyframes pulse {
              0% { opacity: 1; }
              50% { opacity: 0.4; }
              100% { opacity: 1; }
            }
          `}
                </style>
            </Box>
        );
    }

    // 데이터 없음
    if (!data || !availableKPIs?.length) {
        return (
            <Box sx={{ mb: 3 }}>
                <Alert
                    severity="info"
                    icon={<BarChartIcon />}
                    sx={{
                        backgroundColor: '#e3f2fd',
                        border: '1px solid #bbdefb',
                        borderRadius: '12px'
                    }}
                >
                    <Typography variant="body1" fontWeight="500">
                        표시할 KPI 데이터가 없습니다
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        회사별 KPI 구독 정보와 집계 데이터를 확인해주세요.
                    </Typography>
                </Alert>
            </Box>
        );
    }

    // 새로운 데이터 구조에 맞게 조정
    const indicators = data.indicators || [];

    // 지표 데이터가 없는 경우
    if (indicators.length === 0) {
        return (
            <Box sx={{ mb: 3 }}>
                <Paper
                    elevation={1}
                    sx={{
                        p: 3,
                        backgroundColor: '#fff3e0',
                        border: '1px solid #ffcc02',
                        borderRadius: '12px',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                        textAlign: 'center'
                    }}
                >
                    <BarChartIcon sx={{ fontSize: 36, color: '#ff9800', mb: 1 }} />
                    <Typography variant="h6" fontWeight="600" sx={{ color: '#e65100', mb: 1 }}>
                        KPI 지표 데이터가 없습니다
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        해당 지부에서 구독 중인 KPI 지표의 집계 데이터가 아직 생성되지 않았습니다.
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 3 }}>
            {/* 컴팩트한 KPI 요약 카드 */}
            <Box sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                mb: 2
            }}>
                {/* indicators : 모든 정보 (data.indicators) */}
                {indicators
                    .sort((a, b) => {
                        // 가동률증가를 먼저 오도록 정렬
                        const isOperationRateA = a.kpiIndicatorCode === 'A' ||
                            (a.kpiIndicatorName && a.kpiIndicatorName.includes('가동률'));
                        const isOperationRateB = b.kpiIndicatorCode === 'A' ||
                            (b.kpiIndicatorName && b.kpiIndicatorName.includes('가동률'));

                        if (isOperationRateA && !isOperationRateB) return -1;
                        if (!isOperationRateA && isOperationRateB) return 1;

                        // 나머지는 기존 순서 유지
                        return 0;
                    })
                    .map((indicator, index) => (
                    <Paper
                        key={`${indicator.categoryCode}-${indicator.kpiIndicatorCode}`}
                        elevation={1}
                        sx={{
                            p: 2,
                            backgroundColor: '#ffffff',
                            border: '1px solid #e0e0e0',
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                            }
                        }}
                    >
                        {/* KPI 정보 헤더 */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            {/* KPI 지표 이름, 카테고리 이름 */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    {/* KPI 지표 이름 */}
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight="600"
                                        sx={{
                                            color: '#333',
                                            fontSize: '0.95rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {indicator.kpiIndicatorName || indicator.kpiIndicatorCode}
                                    </Typography>

                                    {/* KPI 카테고리 종류 */}
                                    <Chip
                                        label={indicator.categoryName || CATEGORY_LABELS[indicator.categoryCode] || indicator.categoryCode}
                                        size="small"
                                        sx={{
                                            fontSize: '0.65rem',
                                            height: '22px',
                                            backgroundColor: '#e3f2fd',
                                            color: '#1976d2',
                                            borderRadius: '11px'
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* 평균 달성률 */}
                            <Box sx={{ textAlign: 'right', ml: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                    <Typography
                                        variant="h4"
                                        fontWeight="bold"
                                        sx={{
                                            color: '#1976d2',
                                            lineHeight: 1
                                        }}
                                    >
                                        {(() => {
                                            const calculatedAverage = indicator.measuredValue ? Math.floor(indicator.measuredValue * 100) / 100 : 0;
                                            return calculatedAverage.toFixed(2);
                                        })()} {indicator.unit || ''}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body2" color="text.secondary" sx={styles.averageText}>
                                            평균
                                        </Typography>
                                        <Tooltip
                                            title={
                                                <Box sx={{ p: 1 }}>
                                                    <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                                                        📊 KPI 측정값 평균
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                        • 현재 값: {(() => {
                                                            const calculatedAverage = indicator.measuredValue ? Math.floor(indicator.measuredValue * 100) / 100 : 0;
                                                            return calculatedAverage.toFixed(2);
                                                        })()} {indicator.unit || ''}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: '#9e9e9e' }}>
                                                        ※ 성취율이 아닌 실제 측정값 기준
                                                    </Typography>
                                                </Box>
                                            }
                                            placement="top"
                                            arrow
                                            enterDelay={300}
                                            leaveDelay={100}
                                            disableInteractive
                                        >
                                            <HelpOutlineIcon
                                                sx={{
                                                    fontSize: 14,
                                                    color: '#9e9e9e',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        color: '#1976d2'
                                                    }
                                                }}
                                            />
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>


                        <Grid container spacing={2}>
                            {/* 최고 달성률 */}
                            <Grid item xs={12} sm={6} md={4}>
                                <Card sx={{ height: '100%', }}>
                                    <CardContent sx={{ height: '100%', }}>
                                        <Box sx={{ flex: 1,  textAlign: 'center',  height: '100%', }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                <Typography variant="body2" sx={ styles.maxMinRatingCard.maxMinSubscribingTypography }>
                                                    최고 성취율
                                                </Typography>
                                                <Tooltip
                                                    title={
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                                                성취율 계산 방식
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                                • <strong>증가 목표:</strong> (측정값 - 현재값) / 현재값 × 100
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                                • <strong>감소 목표:</strong> (현재값 - 측정값) / 현재값 × 100
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                * 현재값이 0인 경우: 측정값 / 목표값 × 100
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    placement="top"
                                                    arrow
                                                >
                                                    <HelpOutlineIcon sx={{ fontSize: 12, color: '#2e7d32', cursor: 'help' }} />
                                                </Tooltip>
                                            </Box>

                                            <div style={{ display: "flex", justifyContent: "center", alignItems: "anchor-center", }}>
                                                {getMaxMinPercent(indicator.maxAchievement)}
                                                {getTrendingArrow(indicator.maxAchievement)}
                                            </div>

                                            {(() => {
                                                const rankings = getCompanyRankings(indicator.kpiIndicatorCode, indicator.categoryCode);
                                                const topCompany = rankings[0];
                                                return topCompany ? (
                                                    getUpOrDownColorCompanyNameText(topCompany.companyName, indicator.maxAchievement)
                                                ) : null;
                                            })()}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Card sx={{ height: '100%', }}>
                                    <CardContent sx={{ height: '100%', }}>
                                        {/* 최저 달성률 */}
                                        <Box sx={{
                                            flex: 1,
                                            textAlign: 'center',
                                            height: '100%',
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                <Typography variant="body2" sx={ styles.maxMinRatingCard.maxMinSubscribingTypography }>
                                                    최저 성취율
                                                </Typography>
                                                <Tooltip
                                                    title={
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                                                성취율 계산 방식
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                                • <strong>증가 목표:</strong> (측정값 - 현재값) / 현재값 × 100
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                                • <strong>감소 목표:</strong> (현재값 - 측정값) / 현재값 × 100
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                * 현재값이 0인 경우: 측정값 / 목표값 × 100
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    placement="top"
                                                    arrow
                                                >
                                                    <HelpOutlineIcon sx={{ fontSize: 12, color: '#d32f2f', cursor: 'help' }} />
                                                </Tooltip>
                                            </Box>

                                            <div style={{ display: "flex", justifyContent: "center", alignItems: "anchor-center", }}>
                                                {getMaxMinPercent(indicator.minAchievement)}
                                                {getTrendingArrow(indicator.minAchievement)}
                                            </div>

                                            {/* 회사명 */}
                                            {(() => {
                                                const rankings = getCompanyRankings(indicator.kpiIndicatorCode, indicator.categoryCode);
                                                const bottomCompany = rankings[rankings.length - 1];
                                                return bottomCompany ? (
                                                    getUpOrDownColorCompanyNameText(bottomCompany.companyName, indicator.minAchievement)
                                                ) : null;
                                            })()}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Card sx={{ height: '100%', }}>
                                    <CardContent sx={{ height: '100%', }}>
                                        <Box sx={{
                                            flex: 1,
                                            textAlign: 'center',
                                            height: '100%',
                                        }}>
                                            <Typography variant="body2" sx={ styles.maxMinRatingCard.maxMinSubscribingTypography }>
                                                구독중 업체
                                            </Typography>
                                            <Typography variant="h6" fontWeight="bold" sx={{ ...styles.maxMinRatingCard.maxMinSubscribingRateOrNum, color: '#1976d2' }}>
                                                {indicator.totalCompanies}
                                            </Typography>

                                            {/* 달성률 순위 팝업 버튼 */}
                                            <Button
                                                onClick={(e) => handleOpenRankingPopover(e, indicator.kpiIndicatorCode, indicator.categoryCode)}
                                                size="small"
                                                variant="outlined"
                                                sx={styles.maxMinRatingCard.ranking.button}
                                            >
                                                🏆️ 순위
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* 지부별 목표 평균 대비 달성률 진행률 바 */}
                        <Box sx={{ width: '100%', mb: 1 }}>
                            {getLinearProgressBar(indicator)}
                        </Box>

                        {/* 진행률 계산 방식 설명 */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                            <Tooltip
                                title={
                                    <Box sx={{ p: 1.5, maxWidth: 350 }}>
                                        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                                            📈 진행률 계산 방식
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.4 }}>
                                            <strong>1단계:</strong> 해당 KPI를 구독하는 모든 회사의 목표치를 합산
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.4 }}>
                                            <strong>2단계:</strong> 목표치 합산값 ÷ 구독 회사 수 = 지표 목표 평균
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.4 }}>
                                            <strong>3단계:</strong> 현재 실제 측정값 평균 ÷ 지표 목표 평균 × 100 = 달성률(%)
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#9e9e9e', fontSize: '0.8rem' }}>
                                            ※ 지부 내 모든 구독 회사의 목표 수준 대비 현재 실제 성과를 백분율로 표시
                                        </Typography>
                                    </Box>
                                }
                                placement="top"
                                arrow
                                enterDelay={300}
                                leaveDelay={100}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#9e9e9e',
                                        fontSize: '0.75rem',
                                        cursor: 'pointer',
                                        textDecoration: 'underline dotted',
                                        '&:hover': {
                                            color: '#1976d2'
                                        }
                                    }}
                                >
                                    목표 평균 대비 달성률 {Math.round(calculateProgressRate(indicator))}%
                                </Typography>
                            </Tooltip>
                        </Box>

                        {/* 달성률 순위 팝업 */}
                        <Popover
                            open={Boolean(popoverAnchor && selectedIndicatorForRanking === `${indicator.categoryCode}-${indicator.kpiIndicatorCode}`)}
                            anchorEl={popoverAnchor}
                            onClose={handleCloseRankingPopover}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            sx={{
                                '& .MuiPaper-root': {
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                    borderRadius: '12px',
                                    border: '1px solid #e0e0e0'
                                }
                            }}
                        >
                            <Box sx={{ p: 2, minWidth: '280px', maxHeight: '300px', overflow: 'auto' }}averageText>
                                <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2, color: '#333' }}>
                                    {indicator.kpiIndicatorName} 성취율 순위
                                </Typography>
                                <List dense>
                                    {(() => {
                                        const rankings = getCompanyRankings(indicator.kpiIndicatorCode, indicator.categoryCode);
                                        let currentRank = 1;
                                        let sameValueCount = 0;

                                        return rankings.map((company, index) => {
                                            const isFirstOrDifferentValue = index === 0 || rankings[index - 1].achievementRate !== company.achievementRate;
                                            const hasNextSameValue = index < rankings.length - 1 && rankings[index + 1].achievementRate === company.achievementRate;
                                            const hasPrevSameValue = index > 0 && rankings[index - 1].achievementRate === company.achievementRate;

                                            if (isFirstOrDifferentValue) {
                                                currentRank = index + 1;
                                                sameValueCount = 1;
                                            } else {
                                                sameValueCount++;
                                            }

                                            // 공동 순위 여부 확인
                                            const isTiedRank = hasNextSameValue || hasPrevSameValue;

                                            return (
                                                <ListItem key={company.companyName} sx={{ py: 0.5, px: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                        <Typography variant="body2" fontWeight="bold" sx={{
                                                            color: currentRank <= 3 ? ['#FFD700', '#C0C0C0', '#CD7F32'][currentRank - 1] : '#666',
                                                            minWidth: '60px',
                                                            mr: 1,
                                                            fontSize: '0.8rem'
                                                        }}>
                                                            {currentRank}위{isTiedRank ? '(공동)' : ''}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ flex: 1, fontSize: '0.85rem' }}>
                                                            {company.companyName}
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight="600" sx={{
                                                            color: getAchievementColor(company.achievementRate),
                                                            fontSize: '0.8rem'
                                                        }}>
                                                            {formatPercent(company.achievementRate, 1)}
                                                        </Typography>
                                                    </Box>
                                                </ListItem>
                                            );
                                        });
                                    })()}
                                </List>
                            </Box>
                        </Popover>
                    </Paper>
                ))}
            </Box>
        </Box>
    );
};

export default AllKPIStatistics;